import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Button — Putty's general-purpose pill button.
 *
 * Structure ported from Loaf MMM's `InputButton`
 * (`projects/mmm/client/packages/shared/write/components/input/Button/InputButton.tsx`),
 * adapted to Putty conventions: kebab-case file/class names, no sfx, no
 * mobile-specific overrides, brand colors driven by the `--putty-blue|green|red-rgb`
 * tokens defined in the viewer's `_colors.scss` (with safe fallbacks if those
 * tokens aren't present).
 *
 * Three orthogonal axes:
 *   - `vibe`  — visual treatment: `primary` (filled), `secondary` (outlined),
 *               `destructive` (red filled), `neutral` (black filled).
 *   - `tone`  — brand color used for the `primary` fill: `blue | green | red`.
 *   - `size`  — `nano | mini | small | regular | large`.
 *   - `theme` — `light | dark` surface for `secondary` (outline color, etc).
 */
import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import useMergedRef from "@react-hook/merged-ref";
import classnames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { FaIcon } from "@shared/client/components/fa-icon/fa-icon";
import { memoForwardRef } from "@shared/client/util/memo";
import "./button.scss";
export const Button = memoForwardRef(({ id, children, label, onClick, onPointerDown, className, style, vibe = "primary", tone = "green", size = "regular", theme = "light", type = "button", disabled = false, loading = false, withMarginTop = false, wrapWidthToContent = false, fattyWidth = false, focusOnMount = false, noOutlineOnFocus = false, preserveFocus = false, }, ref) => {
    const innerRef = useRef(null);
    const mergedRef = useMergedRef(ref, innerRef);
    useEffect(() => {
        if (focusOnMount === false)
            return;
        innerRef.current?.focus();
    }, [focusOnMount]);
    // The explicit `focus()` here normalizes Safari, which otherwise won't
    // focus buttons on mouse press. `preserveFocus` opts out for callers
    // that need focus to stay on whatever element they came from (e.g. an
    // editor with the mobile keyboard up) — without that escape hatch, the
    // press blurs the editor mid-tap and the viewport reflow eats the
    // click. The pointerdown `preventDefault()` stays in both modes so
    // there's no native focus shift either way.
    const handlePointerDown = useCallback((event) => {
        event.preventDefault();
        if (onPointerDown)
            onPointerDown(event);
        if (preserveFocus === false) {
            innerRef.current?.focus();
        }
    }, [onPointerDown, preserveFocus]);
    const handleClick = useCallback((event) => {
        if (onClick)
            onClick(event);
    }, [onClick]);
    const memoClassName = useMemo(() => classnames("button", vibe, theme, size, `tone-${tone}`, className, {
        "fit-content": wrapWidthToContent,
        "fatty-width": fattyWidth,
        disabled,
        loading,
        "no-outline-on-focus": noOutlineOnFocus,
    }), [
        vibe,
        theme,
        size,
        tone,
        className,
        wrapWidthToContent,
        fattyWidth,
        disabled,
        loading,
        noOutlineOnFocus,
    ]);
    const memoStyle = useMemo(() => {
        const marginTop = withMarginTop ? { marginTop: 16 } : {};
        return { ...marginTop, ...style };
    }, [style, withMarginTop]);
    return (_jsxs("button", { id: id, type: type, ref: mergedRef, className: memoClassName, style: memoStyle, disabled: disabled, onPointerDown: handlePointerDown, onClick: handleClick, children: [_jsx("span", { className: classnames("button__label", { hidden: children !== undefined }), children: label }), children, loading && (_jsx("span", { className: "button__loading-indicator", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "fa-spin" }) }))] }));
});
Button.displayName = "Button";
